import React, { useState } from 'react';

import { CitySearchPanel } from '@/features/leads/components/CitySearchPanel';
import { useCitySearchForLeads, useHomeCountryId } from '@/features/leads/use-lead-options';

interface InvoiceCityPickerProps {
  /// Currently-selected city id (form state). Null when empty.
  value: string | null;
  /// Label to echo for the current selection without a refetch (`"<name> (<isoCode>)"`).
  valueLabel: string | null;
  /// Fires with both id and label so the parent can store the id AND the display label.
  onChange: (next: { id: string | null; label: string | null }) => void;
  label?: string;
  /// Departure = true (Indian cities only, like the lead form's departure picker);
  /// Destination = false (all active cities, so international trips resolve).
  scopeToHome?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: string;
}

/**
 * City picker for the invoice booking section - the SAME server-side city search
 * the lead creation form uses (thin wrapper over `CitySearchPanel` + the shared
 * `useCitySearchForLeads` hook, which enforces active city + active country).
 * `scopeToHome` locks it to the home country (departure) or leaves it global
 * (destination).
 */
export const InvoiceCityPicker = ({
  value,
  valueLabel,
  onChange,
  label = 'City',
  scopeToHome = false,
  disabled,
  required,
  error,
}: InvoiceCityPickerProps): React.JSX.Element => {
  const [term, setTerm] = useState('');
  const homeCountryId = useHomeCountryId();
  const { cities, isLoading, isLoadingMore, hasMore, loadMore } = useCitySearchForLeads({
    term,
    countryId: scopeToHome ? homeCountryId : null,
  });

  return (
    <CitySearchPanel
      value={value}
      valueLabel={valueLabel}
      onChange={onChange}
      label={label}
      emptyHint="Showing the first 20 active cities. Type to narrow."
      cities={cities}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      term={term}
      onTermChange={setTerm}
      disabled={disabled}
      required={required}
      error={error}
    />
  );
};
